import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Input,
  InputSelect,
  Label,
  InputError,
} from '@features/ui';
import {
  AffiliatePayoutSettingsFormData,
  affiliatePayoutSettingsSchema,
  PayoutType,
} from '@src/types/affiliate';
import { createAffiliatePayoutSettings } from '@src/services/affiliate';
import { TOption } from '@src/types';
import { toast } from 'react-toastify';

export const AffiliatesPayoutMethodsPage = () => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
    getValues,
  } = useForm<AffiliatePayoutSettingsFormData>({
    resolver: yupResolver(affiliatePayoutSettingsSchema),
    defaultValues: {
      affiliate_payout_method_id: PayoutType.PayPal,
      affiliate_payout_address: '',
    },
  });

  const watchedPayoutType = watch('affiliate_payout_method_id');

  const payoutTypeOptions: TOption[] = [
    { label: 'PayPal', value: PayoutType.PayPal },
    { label: 'Not Selected', value: PayoutType.Not_Selected },
  ];

  const onSubmit = async (data: AffiliatePayoutSettingsFormData) => {
    try {
      setLoading(true);
      await createAffiliatePayoutSettings({
        affiliate_payout_method_id: data.affiliate_payout_method_id,
        affiliate_payout_address: data.affiliate_payout_address ?? null,
      });
      toast.success('Payout settings updated successfully');
    } catch (error) {
      console.error('Error updating payout settings:', error);
      toast.error('Failed to update payout settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-[23px] font-medium mb-5">Change payout info</h2>
      <Card className="p-6">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6 max-w-[600px]">
            <Controller
              control={control}
              name="affiliate_payout_method_id"
              render={({ field }) => (
                <div className="space-y-2.5">
                  <Label htmlFor="affiliate_payout_method_id" size="sm">
                    Affiliate Payout Type
                  </Label>
                  <InputSelect
                    className="rounded-[10px] border-[#979797] h-11"
                    error={!!errors.affiliate_payout_method_id}
                    id="affiliate_payout_method_id"
                    options={payoutTypeOptions}
                    placeholder="Select payout type"
                    value={field.value}
                    onChange={field.onChange}
                  />
                  {errors.affiliate_payout_method_id && (
                    <InputError>
                      {errors.affiliate_payout_method_id.message}
                    </InputError>
                  )}
                </div>
              )}
            />

            {/* PayPal Email - Only show when PayPal is selected */}
            {watchedPayoutType === PayoutType.PayPal && (
              <Controller
                control={control}
                name="affiliate_payout_address"
                render={({ field }) => (
                  <div className="space-y-2.5">
                    <Label htmlFor="affiliate_payout_address" size="sm">
                      Affiliate Payout - Paypal E-Mail address
                      <span className="text-muted-foreground text-xs font-normal ml-1">
                        for affiliate commission payouts
                      </span>
                    </Label>
                    <Input
                      className="rounded-[10px] border-[#979797] h-11"
                      error={!!errors.affiliate_payout_address}
                      id="affiliate_payout_address"
                      placeholder="Affiliate Payout - Paypal E-Mail address"
                      type="email"
                      {...field}
                      value={field.value || ''}
                    />
                    {errors.affiliate_payout_address && (
                      <InputError>
                        {errors.affiliate_payout_address.message}
                      </InputError>
                    )}
                  </div>
                )}
              />
            )}

            <div className="flex justify-start pt-2">
              <Button
                className="min-w-[100px]"
                disabled={
                  !isDirty ||
                  loading ||
                  getValues().affiliate_payout_method_id ===
                    PayoutType.Not_Selected
                }
                size="sm"
                type="submit"
                variant="lime"
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};
